import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MainLayout } from '@templates/index';
import { Button, Card } from '@atoms/index';
import { formatCurrency } from '@utils/currency';
import { useOrderSocket } from '@/hooks/useOrderSocket';
import { orderService } from '@services/orderService';

/**
 * Payment Success Page
 * 
 * Displays success message after successful payment.
 * Listens for PAYMENT_SUCCESS WebSocket event and auto-redirects to tracking.
 */
const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [orderNumber, setOrderNumber] = useState<string | null>(null);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showAnimation, setShowAnimation] = useState(true);

  // Get order ID from URL or fetch by order number
  useEffect(() => {
    const orderNum = searchParams.get('orderNumber');
    const status = searchParams.get('status');
    const id = searchParams.get('orderId');

    if (status === 'success' && orderNum) {
      setOrderNumber(orderNum);
      if (id) {
        setOrderId(id);
        setIsLoading(false);
      } else {
        // Try to find order by order number from user orders
        orderService
          .getUserOrders()
          .then((orders) => {
            const foundOrder = orders.find((o) => o.orderNumber === orderNum);
            if (foundOrder) {
              setOrderId(foundOrder.id);
              setIsLoading(false);
            } else {
              setIsLoading(false);
            }
          })
          .catch(() => {
            setIsLoading(false);
          });
      }
    } else if (status === 'failed') {
      const error = searchParams.get('error') || 'Ödeme işlemi başarısız oldu.';
      navigate('/checkout?error=' + encodeURIComponent(error));
    } else {
      navigate('/');
    }
  }, [searchParams, navigate]);

  // Listen for PAYMENT_SUCCESS event
  const { on, off, isConnected } = useOrderSocket(orderId);

  useEffect(() => {
    if (!isConnected || !orderId) return;

    const handlePaymentSuccess = (data: { orderId: string; orderNumber: string }) => {
      if (data.orderId === orderId) {
        console.log('✅ Payment success event received:', data);
        // Auto-redirect to tracking after 2 seconds
        setTimeout(() => {
          navigate(`/orders/${orderId}`);
        }, 2000);
      }
    };

    on('PAYMENT_SUCCESS', handlePaymentSuccess);

    return () => {
      off('PAYMENT_SUCCESS', handlePaymentSuccess);
    };
  }, [isConnected, orderId, on, off, navigate]);

  const handleViewOrder = () => {
    // Navigate to home page (orders list can be added later)
    // For now, redirect to home where user can see their orders
    navigate('/');
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-gray-600">Yükleniyor...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <Card className="p-8 text-center">
          {/* Success Icon with Animation */}
          <div className="mb-6">
            {showAnimation ? (
              <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center animate-bounce">
                <svg
                  className="w-12 h-12 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            ) : (
              <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-12 h-12 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            )}
          </div>

          {/* Success Message */}
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Ödeme Başarılı!
          </h1>
          <p className="text-lg text-gray-600 mb-8">
            Siparişiniz alındı ve ödemeniz tamamlandı.
          </p>

          {/* Order Number */}
          {orderNumber && (
            <div className="bg-gray-50 rounded-lg p-6 mb-8">
              <p className="text-sm text-gray-600 mb-2">Sipariş Numarası</p>
              <p className="text-2xl font-bold text-primary">{orderNumber}</p>
            </div>
          )}

          {/* Info Message */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
            <p className="text-sm text-blue-800">
              Siparişiniz hazırlanmaya başlandı. Sipariş durumunu takip edebilirsiniz.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {orderId && (
              <Button
                onClick={() => navigate(`/orders/${orderId}`)}
                variant="primary"
              >
                Siparişi Takip Et
              </Button>
            )}
            <Button
              onClick={handleViewOrder}
              variant="secondary"
            >
              Siparişlerimi Görüntüle
            </Button>
            <Button
              onClick={handleBackToHome}
              variant="secondary"
            >
              Ana Sayfaya Dön
            </Button>
          </div>
        </Card>
      </div>
    </MainLayout>
  );
};

export default PaymentSuccessPage;
